<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm font-semibold text-gray-700">
        Recent outbound integration calls (Redis, ~180-day retention).
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="fetch">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">Service</label>
        <select
          v-model="serviceName"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          :disabled="loading"
          @change="onFilterChange"
        >
          <option value="">All services</option>
          <option v-for="name in INTEGRATION_SERVICE_NAMES" :key="name" :value="name">
            {{ name }}
          </option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">Page size</label>
        <select
          v-model.number="pageSize"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          :disabled="loading"
          @change="onPageSizeChange"
        >
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
      </div>
    </div>

    <div class="relative min-h-[320px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading audit logs...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Service</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Operation</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Method</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">HTTP</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Time</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Timestamp</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 max-w-[12rem]">URL</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-28">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in audits"
                  :key="row.id || idx"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-100">{{ rowNumber(idx) }}</td>
                  <td class="py-3 px-4 border-b border-gray-100 font-semibold">{{ row.serviceName || "—" }}</td>
                  <td class="py-3 px-4 border-b border-gray-100">{{ row.operation || "—" }}</td>
                  <td class="py-3 px-4 border-b border-gray-100 font-mono">{{ row.httpMethod || "—" }}</td>
                  <td class="py-3 px-4 border-b border-gray-100 font-mono">{{ row.responseStatus ?? "—" }}</td>
                  <td class="py-3 px-4 border-b border-gray-100">
                    <span
                      class="px-2 py-0.5 rounded-full text-xs font-semibold"
                      :class="auditStatusBadgeClass(row.status)"
                    >
                      {{ row.status || "—" }}
                    </span>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ row.executionTimeMs != null ? `${row.executionTimeMs} ms` : "—" }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ formatTimestamp(row) }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 max-w-[12rem]">
                    <p class="truncate font-mono" :title="row.outgoingUrl || undefined">
                      {{ row.outgoingUrl || "—" }}
                    </p>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 text-center">
                    <button
                      type="button"
                      class="btn-sm-primary whitespace-nowrap"
                      :disabled="!row.id || detailLoadingId === row.id"
                      @click="viewDetails(row)"
                    >
                      <i
                        class="fa-solid mr-1"
                        :class="detailLoadingId === row.id ? 'fa-spinner fa-spin' : 'fa-eye'"
                      ></i>
                      View
                    </button>
                  </td>
                </tr>
                <tr v-if="loaded && !loading && audits.length === 0">
                  <td colspan="10" class="py-8 px-4 text-center text-gray-500">
                    No audit logs found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs text-gray-600">
          <p>
            Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalElements }}
          </p>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="btn"
              :disabled="loading || page <= 0"
              @click="previousPage"
            >
              Previous
            </button>
            <span class="px-2 py-1 bg-primary text-white rounded">
              {{ page + 1 }} / {{ Math.max(totalPages, 1) }}
            </span>
            <button
              type="button"
              class="btn"
              :disabled="loading || page + 1 >= totalPages"
              @click="nextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <AppModal v-model="showDetail" title="Audit Log Details" size="lg" hide-footer>
      <div v-if="selectedAudit" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
          <p class="break-all"><span class="font-semibold text-gray-500 mr-2">ID:</span>{{ selectedAudit.id || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Service:</span>{{ selectedAudit.serviceName || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Operation:</span>{{ selectedAudit.operation || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Caller IP:</span>{{ selectedAudit.callerIp || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Method:</span>{{ selectedAudit.httpMethod || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">HTTP status:</span>{{ selectedAudit.responseStatus ?? "—" }}</p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Status:</span>
            <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="auditStatusBadgeClass(selectedAudit.status)">
              {{ selectedAudit.status || "—" }}
            </span>
          </p>
          <p><span class="font-semibold text-gray-500 mr-2">Duration:</span>{{ selectedAudit.executionTimeMs != null ? `${selectedAudit.executionTimeMs} ms` : "—" }}</p>
          <p class="md:col-span-2 break-all"><span class="font-semibold text-gray-500 mr-2">URL:</span>{{ selectedAudit.outgoingUrl || "—" }}</p>
          <p class="md:col-span-2"><span class="font-semibold text-gray-500 mr-2">Timestamp:</span>{{ formatTimestamp(selectedAudit) }}</p>
          <p class="md:col-span-2 break-all"><span class="font-semibold text-gray-500 mr-2">Error:</span>{{ selectedAudit.errorMessage || "—" }}</p>
        </div>

        <div v-if="selectedAudit.requestHeaders">
          <h4 class="font-semibold text-gray-700 mb-2">Request headers</h4>
          <pre class="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto max-h-40 whitespace-pre-wrap break-all">{{ formatJson(selectedAudit.requestHeaders) }}</pre>
        </div>
        <div v-if="selectedAudit.requestPayload">
          <h4 class="font-semibold text-gray-700 mb-2">Request payload</h4>
          <pre class="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{{ formatPayload(selectedAudit.requestPayload) }}</pre>
        </div>
        <div v-if="selectedAudit.responsePayload">
          <h4 class="font-semibold text-gray-700 mb-2">Response payload</h4>
          <pre class="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{{ formatPayload(selectedAudit.responsePayload) }}</pre>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast, useUtilities } from "@/composables";
import {
  INTEGRATION_SERVICE_NAMES,
  auditStatusBadgeClass,
  extractErrorMessage,
  fetchIntegrationAuditById,
  fetchIntegrationAudits,
  type IntegrationAuditRecord,
} from "../monitoringApi";

const { errorToast } = useToast();
const { dateFormat } = useUtilities();

const loading = ref(false);
const loaded = ref(false);
const audits = ref<IntegrationAuditRecord[]>([]);
const serviceName = ref("");
const page = ref(0);
const pageSize = ref(20);
const totalElements = ref(0);
const totalPages = ref(0);
const showDetail = ref(false);
const selectedAudit = ref<IntegrationAuditRecord | null>(null);
const detailLoadingId = ref<string | null>(null);

const rangeStart = computed(() => {
  if (totalElements.value === 0) return 0;
  return page.value * pageSize.value + 1;
});

const rangeEnd = computed(() => {
  return Math.min((page.value + 1) * pageSize.value, totalElements.value);
});

const rowNumber = (idx: number) => page.value * pageSize.value + idx + 1;

const formatTimestamp = (row: IntegrationAuditRecord) => {
  if (row.formattedTimestamp) return row.formattedTimestamp;
  if (row.timestamp != null) return dateFormat(new Date(row.timestamp).toISOString()) || String(row.timestamp);
  return "—";
};

const formatJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatPayload = (value: string) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const fetch = async () => {
  loading.value = true;
  try {
    const result = await fetchIntegrationAudits({
      serviceName: serviceName.value || undefined,
      page: page.value,
      size: pageSize.value,
    });
    audits.value = result.content;
    totalElements.value = result.totalElements;
    totalPages.value = result.totalPages;
    // If API returns only the current page array without totals, estimate paging.
    if (result.totalPages <= 1 && result.content.length === pageSize.value) {
      totalPages.value = page.value + 2;
      totalElements.value = Math.max(totalElements.value, (page.value + 1) * pageSize.value + 1);
    }
  } catch (error: unknown) {
    audits.value = [];
    totalElements.value = 0;
    totalPages.value = 0;
    errorToast("Error", extractErrorMessage(error, "Could not load integration audit logs"));
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const onFilterChange = () => {
  page.value = 0;
  fetch();
};

const onPageSizeChange = () => {
  page.value = 0;
  fetch();
};

const previousPage = () => {
  if (page.value <= 0) return;
  page.value -= 1;
  fetch();
};

const nextPage = () => {
  if (page.value + 1 >= totalPages.value) return;
  page.value += 1;
  fetch();
};

const viewDetails = async (row: IntegrationAuditRecord) => {
  if (!row.id) return;
  detailLoadingId.value = row.id;
  try {
    selectedAudit.value = await fetchIntegrationAuditById(row.id);
    showDetail.value = true;
  } catch (error: unknown) {
    // Fall back to row data if by-id fails.
    selectedAudit.value = row;
    showDetail.value = true;
    errorToast("Error", extractErrorMessage(error, "Could not load full audit details"));
  } finally {
    detailLoadingId.value = null;
  }
};

onMounted(() => fetch());

defineExpose({ refresh: fetch });
</script>
